<template>
  <div class="customization-upload-image-wrapper">
    <div v-if="customConfig?.length">
      <div v-for="(config, i) in customConfig" :key="i">
        <div v-if="config && config.type == 'options'" class="mb-sm">
          <div class="customization-upload-image-upload-title">
            {{ config?.title || 'Choose Option *' }}
          </div>
          <sib-select size="md" :options="config?.value?.split(',').map((item: any) => ({ value: item, label: item }))"
            :value="config.customValue" @input="(val:string) => {config.customValue = val}">
          </sib-select>
        </div>
        <div v-else-if="config && config.type == 'text'" class="mb-sm">
          <div class="customization-upload-image-upload-title">
            {{ config?.title || 'Enter Name *' }}
          </div>
          <sib-input v-model="config.customValue" :placeholder="config?.value || 'Eg: Emma, Lyly, ...'" size="md"
            round="md"></sib-input>
        </div>
      </div>
    </div>
    <div v-else-if="customName" class="mb-sm">
      <div class="customization-upload-image-upload-title">
        Enter Your Name *
      </div>
      <sib-input v-model="customYourName" size="md" round="md"></sib-input>
    </div>
    <div v-if="images && images?.number > 1">
      <div v-if="images && images.type != 'fixed'" class="customization-upload-image-upload-title">Select Number of
        Photos
      </div>
      <sib-select v-if="images && images.type != 'fixed'" size="md" :options="imageNumberUploadOptions"
        :value="imageNumberUpload" @input="changeImageNumberUploadOption">
      </sib-select>

      <div v-for="(item, i) in Number(imageNumberUpload)" :key="i" class="custom-upload-image-style-transfer">
        <StyleTransferUpload v-bind="{
          title: `Upload Photo #${i + 1}`,
          id: `${id}-${i}`,
          selectImageButtonCustomily,
          customizationTemplate,
          photoNumber: i + 1,
          listImageUploadSelected
        }">
        </StyleTransferUpload>
      </div>

      <div class="mt-sm" style="display: flex; align-items: center;" :class="{'disable-button': loading}">
        <div
          class="generate-button sib-button sib-button--primary sib-button--md sib-button--width-default sib-button--round-md"
          @click="transferMultipleImage">
          <div v-if="loading" class="generate-loading lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div>Generate Preview</div>
        </div>
        <span style="height: 40px;transform: translateY(2px);" class="tooltip-image-processing" v-show="loading">
          {{ imageProcessingText }}
          {{ percentProcessed ? `${percentProcessed}%` : '' }}
        </span>
        <span style="height: 40px;line-height: 1;" class="remove-custom-upload-image"
          v-show="isSuccessUpload && !loading" @click="removeImage">X</span>
      </div>
      <div class="customization-upload-image-wrapper-message" v-show="isShowMessage && !isRemoveImage"
        :class="messageClasses">
        {{ messageImageUpload }}
      </div>

      <div v-show="titleCustomConfigHasNotValue"
        class="customization-upload-image-wrapper-message text-secondary mt-xs">
        {{ `${titleCustomConfigHasNotValue?.replace('*', '').trim() } is required` }}
      </div>

      <div v-show="!hasCustomYourNameRequired" class="customization-upload-image-wrapper-message text-secondary mt-xs">
        {{ `Your Name is required` }} <br />
      </div>

      <div class="customization-upload-image-wrapper-message text-secondary mt-xs"
        v-show="!isEnoughNumberImagesRequired">
        {{ `You need ${imageNumberUpload} photos to start generating.` }}
      </div>
    </div>
    <template v-else>
      <div v-if="customName || customConfig?.length" class="customization-upload-image-upload-title mt-sm">
        Upload Photo
      </div>
      <RadioGroup v-if="isShowOptions" type="image" :group="id" :options="options" :loading="loading"
        v-model="templateIdSelected" class="mb-sm"></RadioGroup>
      <CustomFileUpload :id="id">
      </CustomFileUpload>
      <span class="tooltip-image-processing" v-show="loading">
        {{ imageProcessingText }}
        {{ percentProcessed ? `${percentProcessed}%` : '' }}
      </span>
      <span class="remove-custom-upload-image" v-show="isSuccessUpload && !loading" @click="removeImage">X</span>
      <span class="customization-upload-image-wrapper-message" v-show="isShowMessage && !isRemoveImage"
        :class="messageClasses">
        {{ messageImageUpload }}
      </span>

      <div v-show="!hasCustomYourNameRequired" class="customization-upload-image-wrapper-message text-secondary mt-xs">
        <br /> Please enter your name.
      </div>
      <div v-show="titleCustomConfigHasNotValue" class="customization-upload-image-wrapper-message text-secondary mt-xs">
        <br /> {{ `${titleCustomConfigHasNotValue?.replace('*', '').trim()} is required` }}
      </div>
    </template>

    <div class="rate-limit-transfer-message" v-show="exceedTheLimit">
      <b>You’ve used all your free previews.</b>
      <br />Add this product to cart to unlock <b>10 more preview attempts.</b>
    </div>

    <div v-if="aiStyleTransferRecently?.length">
      <div class="ai-style-transfer-history-title">History</div>
      <div class="ai-style-transfer-recently">
        <div v-for="(item, index) in aiStyleTransferRecently" :key="index" class="ai-style-transfer-recently__item"
          :class="{
            'ai-style-transfer-recently__item--active': aiStyleTransferRecentlyIndexSelected == index
          }" @click="chooseImageAiStyleTransferRecently(index)">
          <div class="ai-style-transfer-recently__item--image">
            <img :src="item.image" />
          </div>
        </div>
      </div>
    </div>

    <input type="hidden" v-if="imageUploadUrl" :name="`properties[_style-transfer-image-upload-url]`"
      :value="imageUploadUrl" />
  </div>
</template>

<script lang="ts" setup>
import { computed, h, onMounted, ref, watch, onBeforeMount } from 'vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import RadioGroup from '@/ui-kits/RadioGroup.vue'
import Logger from '@/services/log'
import useCustomizationStore from '@/store/customization'
import { useRoute } from 'vue-router'
import {
  CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS,
  EVENT_CUSTOM_UPLOAD_IMAGE,
  QUERY_STRING_NO_CACHE,
  EVENT_INCREASE_AI_TRANSFER_LIMITED,
  EVENT_CUSTOM_UPLOAD_STYLE_TRANSFER_IMAGE,
  SEVEN_DAYS_IN_MILLISECONDS,
  REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
  CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR,
  cloneDeep
} from '@/utils'
import EventBus from '@/services/eventbus'
import { CustomizationTemplate } from '@/types'
import { useProductCustomization } from './composables/ProductCustomization'
import useProductStore from '@/store/product'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import { isProductHasTags } from '@/utils/product'
import StyleTransferUpload from './StyleTransferUpload.vue'

const productStore = useProductStore()
const customizationStore = useCustomizationStore()
const route = useRoute()
const {
  convertImageLinkToFileList,
  changeFileImageCustomily,
  startUpload,
  finishUpload,
  percentProcessed,
} = useProductCustomization()

const props = withDefaults(
  defineProps<{
    id: string
    inputFileCustomily: Element
    selectImageButtonCustomily: Element
    removeImageButtonCustomily: HTMLElement
    customizationTemplate: CustomizationTemplate
    optionSortId: string
    customName?: string | number | null
    customilyElementWrapper: HTMLElement
    images?: any,
    custom?: any
  }>(),
  {}
)

let options = props.customizationTemplate.templates.map((option) => ({
  id: option.template_id,
  name: option.name || option.title,
  image: option.image,
  value: option.template_id,
}))

const messageImageUpload = computed(() =>
  isSuccessUpload.value ? successfullUploadMessage : errorUploadMessage.value
)
const messageClasses = computed(() =>
  isSuccessUpload.value
    ? textPrimaryClass
    : `${textSecondaryClass} ${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS}-message--error`
)

const templateIdDefault = computed(() => {
  const templateIdOriginal = '0000'
  const index = options.findIndex(
    (option) => option.value === templateIdOriginal
  )
  return index > -1 ? templateIdOriginal : options[0].value
})

const isShowOptions = computed(() => options.length > 1)

let loading = ref(false)
let isSuccessUpload = ref(false)
let isShowMessage = ref(false)
let isRemoveImage = ref(false)
const imageProcessingText = 'Processing'
const successfullUploadMessage = 'Success! Enjoy Your Preview'
let errorUploadMessage = ref('Error upload!')
const textPrimaryClass = 'text-primary'
const textSecondaryClass = 'text-secondary'
const sibFileNameStyleTransferImagePrefix = 'sib-file-name-style-transfer-image'
const errorTransferImageMessage =
  'The Image Style could not be done with this image. Please choose another image or use different way of style'

let templateIdSelected = ref(templateIdDefault.value)
let imageSelected = ref('')

const transferLimited = 10
const AI_STYLE_TRANSFER_LIMITED_CACHE_PREFIX = 'AI-style-transfer-limited'
const AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX = 'AI-style-transfer-recently'
const aiStyleTransferRecentlyIndexSelected = ref(-1)
const exceedTheLimit = ref(false)

const aiStyleTransferRecently: any = ref([])
const imageAITransferedLink = ref('')

let imageNumberUploadOptions: any = [
  {
    value: '1',
    label: '1 photo',
  },
  {
    value: '2',
    label: '2 photos',
  },
  {
    value: '3',
    label: '3 photos',
  },
]
const imageNumberUpload = ref('1')
const listImageUploadSelected: any = ref([])
const isEnoughNumberImagesRequired = ref(true)
const imageUploadUrl = ref('')
const customYourName = ref('')
const hasCustomYourNameRequired = ref(true)
const customConfig:any = ref([])
const isEnoughCustomValueRequired = ref(true)
const titleCustomConfigHasNotValue = ref('')

onBeforeMount(() => {
  if (props.images?.number > 1) {
    imageNumberUploadOptions = []
    const min = Number(props.images?.min) || 1
    const max = Number(props.images?.number) || 0

    for (let i = min; i <= max; i++) {
      imageNumberUploadOptions.push({
        value: `${i}`,
        label: `${i} ${i > 1 ? 'photos' : 'photo'}`,
      })
    }

    imageNumberUpload.value = props.images?.number?.toString()
  }

  if (props.custom?.type == 'options' && props.custom?.value?.split(',')?.length) {
    customYourName.value = props.custom?.value?.split(',')[0]
  }

  if (props.custom?.length) {
    customConfig.value = props.custom.map((item:any) => ({
        title: item?.title,
        type: item?.type,
        value: item?.value,
        prompt: item?.prompt,
        customValue: item?.type == 'options' && item?.value?.split(',')?.length ? item?.value?.split(',')[0] : '',
    }))
  }
})

onMounted(async () => {
  if (customizationStore.styleTransfered.length) {
    const styleTransferedItem = customizationStore.styleTransfered.find(
      (item) => item.optionSortId === props.optionSortId
    )
    if (styleTransferedItem) {
      templateIdSelected.value = styleTransferedItem.templateId
      imageSelected.value = styleTransferedItem.image
    }
  }

  // watch(
  //   () => templateIdSelected.value,
  //   async (value) => {
  //     try {
  //       if (!value || !imageSelected.value) return
  //       loading.value = true
  //       isSuccessUpload.value = false
  //       isShowMessage.value = false

  //       const payload = {
  //         image: imageSelected.value,
  //         templateId: value,
  //         id: props.customizationTemplate.id,
  //       }
  //       const imageTransferedLink = await transferImage(payload)
  //       if (!imageTransferedLink) {
  //         throw {
  //           message: errorTransferImageMessage,
  //         }
  //       }

  //       const fileName = `${sibFileNameStyleTransferImagePrefix}-${templateIdSelected.value}-${props.customizationTemplate.id}`
  //       const imageFileList = await convertImageLinkToFileList(
  //         imageTransferedLink!,
  //         fileName
  //       )
  //       changeFileImageCustomily(imageFileList, props.inputFileCustomily)

  //       isSuccessUpload.value = true
  //       customilyUpLoadingHandler()
  //       saveStyleTransfered()
  //     } catch (err: any) {
  //       errorUploadMessage.value = err.message ?? errorUploadMessage.value
  //       loading.value = false
  //       isSuccessUpload.value = false
  //       isShowMessage.value = true

  //       Logger.log('Error when transfer style image', err)
  //     }
  //   }
  // )

  watch(
    () => loading.value,
    (value) => {
      EventBus.trigger(EVENT_CUSTOM_UPLOAD_IMAGE, value)
      if (value) startUpload()
      else finishUpload()
    }
  )

  await setDefaultAiStyleTransferLimited()
  await getAiStyleTransferRecently()

  EventBus.on(EVENT_INCREASE_AI_TRANSFER_LIMITED, async ()=> {
    await increaseAiStyleTransferLimited()
  })

  if (props.customilyElementWrapper) {
    props.customilyElementWrapper?.classList.add('wrapper-style-transfer')
  }

  EventBus.on(EVENT_CUSTOM_UPLOAD_STYLE_TRANSFER_IMAGE, async (data) => {
    const index = listImageUploadSelected.value.findIndex((item: any) => item.photoNumber === data.photoNumber);
    if (index !== -1) {
      listImageUploadSelected.value[index] = data;
    } else {
      listImageUploadSelected.value.push(data);
    }
    listImageUploadSelected.value.sort((a:any, b:any) => a.photoNumber - b.photoNumber);
  })

  watch([props.images?.number, props.customName, props.custom?.length], () => {
    const customilyOptionWrapper = document.querySelector(
      `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
        'option_sort_id',
        props.optionSortId?.toString()
      )}`
    )
    if (!customilyOptionWrapper) return ""
    const lableOption = customilyOptionWrapper.querySelector(`label`)

    if (props.images?.number > 1 || props.customName || props.custom?.length) {
      if (lableOption) lableOption.style.display = 'none'
    } else { 
      if (lableOption) lableOption.style.display = 'block'
    }
  }, { immediate: true});

  watch(
    () => imageNumberUpload.value,
    () => {
      isEnoughNumberImagesRequired.value = true
    }
  )
})

async function transferImage({
  image,
  templateId,
  id,
  custom_name,
  prompt_custom,
}: {
  image: string
  templateId: string
  id: number,
  custom_name: string,
  prompt_custom: string
}) {
  if (!image || !templateId || !id) return
  const payload = {
    image,
    templateId,
    id,
    custom_name,
    prompt_custom,
    isForceLoadNewData: true,
  }
  const imageTransferedLink = await customizationStore.transferImage(payload)
  return imageTransferedLink
}

const CustomFileUpload = ({ id }: { id: string }) => {
  let tag: any = FileUpload
  const propsCustomFileUpload: {
    [key: string]: any
  } = {}

  propsCustomFileUpload.id = id
  propsCustomFileUpload.loading = loading.value
  propsCustomFileUpload.onChange = onChange

  async function onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const file = input.files[0]
    if (!file) return

    isEnoughCustomValueRequired.value = hasEnoughCustomValueRequired()
    if (!isEnoughCustomValueRequired.value) {
      input.value = ''
      return
    }
    
    hasCustomYourNameRequired.value = checkCustomYourName()
    if (!hasCustomYourNameRequired.value) {
      input.value = ''
      return
    }
   
    await checkNumberOfRemainAiStyleTransfer()
    if (exceedTheLimit.value) {
      input.value = ''
      return
    }
    
    loading.value = true
    isSuccessUpload.value = false
    isShowMessage.value = false
    isRemoveImage.value = false

    const payload = {
      file,
      id: props.customizationTemplate.id,
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    
    customizationStore
      .uploadImageTransferDefault(payload)
      .then(async (response) => {
        if (response.success === false) {
          throw response
        }
        const link = response
        imageSelected.value = link
        imageUploadUrl.value = link

        const promptCustom = customConfig.value.reduce((acc:any, item:any) => {
          acc[item.prompt] = item.customValue || '';
          return acc;
        }, {});
        const payload = {
          image: link!,
          templateId: templateIdSelected.value,
          id: props.customizationTemplate.id,
          custom_name: customYourName.value || '',
          prompt_custom: customConfig.value?.length ? JSON.stringify(promptCustom) : ''
        }
        const imageTransferedLink = await transferImage(payload)
        if (!imageTransferedLink) {
          throw {
            message: errorTransferImageMessage,
          }
        }

        // vì link ảnh AI cần phải mất một thời gian mới có thể active
        // nên cần phải đợi một thời gian để link ảnh được active thì tiếp tục xử lí tiếp
        const isReady = await waitForImageReady(imageTransferedLink)
        if (isReady) {
          imageAITransferedLink.value = imageTransferedLink
        } else {
          throw {
            message: errorTransferImageMessage,
          }
        }

        const fileName = `${sibFileNameStyleTransferImagePrefix}-${templateIdSelected.value}-${props.customizationTemplate.id}`
        const imageFileList = await convertImageLinkToFileList(
          imageTransferedLink!,
          fileName
        )
        changeFileImageCustomily(imageFileList, props.inputFileCustomily)
        isSuccessUpload.value = true
        saveStyleTransfered()
      })
      .catch((err) => {
        errorUploadMessage.value = err.message ?? errorUploadMessage.value
        isSuccessUpload.value = false
        Logger.log('Error when uploading image', err)
      })
      .finally(async () => {
        if (isSuccessUpload.value) {
          customilyUpLoadingHandler()
          await setAiStyleTransferRecently(imageAITransferedLink.value, imageUploadUrl.value)
          imageAITransferedLink.value = ''
          aiStyleTransferRecentlyIndexSelected.value = 0
        } else {
          loading.value = false
          isShowMessage.value = true
        }
      })
    input.value = ''
  }

  return h(tag, propsCustomFileUpload)
}

function customilyUpLoadingHandler() {
  const timeout = setTimeout(() => {
    const isCustomilyUpLoading =
      props.selectImageButtonCustomily.classList.contains('disabled')
    if (isCustomilyUpLoading) {
      customilyUpLoadingHandler()
    } else {
      loading.value = false
      isShowMessage.value = true
      clearTimeout(timeout)
    }
  }, 200)
}

function removeImage() {
  if (!props.customilyElementWrapper) return
  const removeImageButton : any =
    props.customilyElementWrapper.querySelector(
      REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
    )
  if (!removeImageButton) return
  removeImageButton.click()
  imageSelected.value = ''
  isSuccessUpload.value = false
  isRemoveImage.value = true
  exceedTheLimit.value = false
  aiStyleTransferRecentlyIndexSelected.value = -1
  imageUploadUrl.value = ''
}

function saveStyleTransfered() {
  const index = customizationStore.styleTransfered.findIndex(
    (item) => item.optionSortId == props.optionSortId
  )
  const styleTransferedItem = {
    optionSortId: props.optionSortId,
    templateId: templateIdSelected.value,
    image: imageSelected.value,
  }
  if (index > -1) {
    customizationStore.styleTransfered.splice(index, 1, styleTransferedItem)
  } else {
    customizationStore.styleTransfered.push(styleTransferedItem)
  }
}

function getMillisecondsUntilEndOfDay() {
  const now = new Date();
  const endOfDay = new Date(now);

  endOfDay.setHours(23, 59, 59, 999);

  return endOfDay.getTime() - now.getTime();
}

async function setDefaultAiStyleTransferLimited() {
  // set style transfer limited for product
  const productHandle = productStore.product?.handle
  const cacheKey = `${AI_STYLE_TRANSFER_LIMITED_CACHE_PREFIX}_${productHandle}`
  const cacheResponse = await CacheService.instance?.get(cacheKey)
  const aiTransferLimitedFromCache = getJSONfromCacheResponse(cacheResponse)
  if (!aiTransferLimitedFromCache) {
    const ttl = getMillisecondsUntilEndOfDay()
    CacheService.instance?.set(
      cacheKey,
      transferLimited,
      ttl
    )
  }
}

async function increaseAiStyleTransferLimited() {
  const productHandle = productStore.product?.handle
  const cacheKey = `${AI_STYLE_TRANSFER_LIMITED_CACHE_PREFIX}_${productHandle}`
  const cacheResponse = await CacheService.instance?.get(cacheKey)
  const aiTransferLimitedFromCache = getJSONfromCacheResponse(cacheResponse)

  await CacheService.instance?.delete(cacheKey)

  if (aiTransferLimitedFromCache) {
    const ttl = getMillisecondsUntilEndOfDay()
    const aiTransferLimited = Number(aiTransferLimitedFromCache) + transferLimited
    CacheService.instance?.set(
      cacheKey,
      aiTransferLimited,
      ttl
    )
  }
}

async function getAiStyleTransferRecently() {
  const productHandle = productStore.product?.handle
  const cacheKey = `${AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX}_${productHandle}`
  const cacheResponse = await CacheService.instance?.get(cacheKey)
  const aiStyleTransferRecentlyFromCache = getJSONfromCacheResponse(cacheResponse)
  if (aiStyleTransferRecentlyFromCache) {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
   
    aiStyleTransferRecently.value = aiStyleTransferRecentlyFromCache?.filter((item: any) => {
      const itemTime = new Date(item.createdAt).getTime();
      return itemTime >= sevenDaysAgo && itemTime <= now;
    });

    // kiểm tra nếu có item đã bị loại đi thì set lại cache
    if (aiStyleTransferRecently.value?.length != aiStyleTransferRecentlyFromCache?.length) {
      
      await CacheService.instance?.delete(cacheKey)

      CacheService.instance?.set(
        cacheKey,
        cloneDeep(aiStyleTransferRecently.value),
        SEVEN_DAYS_IN_MILLISECONDS
      )
    }
    return aiStyleTransferRecently.value
  }
  return []
}

async function setAiStyleTransferRecently(image: string, imageUploadUrl: string) {
  const productHandle = productStore.product?.handle
  const cacheKey = `${AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX}_${productHandle}`
  const cacheResponse = await CacheService.instance?.get(cacheKey)
  const aiStyleTransferRecentlyFromCache = getJSONfromCacheResponse(cacheResponse)
  await CacheService.instance?.delete(cacheKey)

  if (aiStyleTransferRecentlyFromCache?.length) {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;

    aiStyleTransferRecently.value = aiStyleTransferRecentlyFromCache?.filter((item: any) => {
      const itemTime = new Date(item.createdAt).getTime();
      return itemTime >= sevenDaysAgo && itemTime <= now;
    });
  } 
  
  aiStyleTransferRecently.value = [
    { image, createdAt: Date.now(), imageUploadUrl },
    ...(aiStyleTransferRecently.value || [])
  ]
  CacheService.instance?.set(
    cacheKey,
    cloneDeep(aiStyleTransferRecently.value),
    SEVEN_DAYS_IN_MILLISECONDS
  )
}

async function chooseImageAiStyleTransferRecently(index: number) {
  aiStyleTransferRecentlyIndexSelected.value = index
  const imageLinkRecentlySelected = aiStyleTransferRecently.value[index]?.image
  const fileName = `${sibFileNameStyleTransferImagePrefix}-ai-${index}`
  const imageFileList = await convertImageLinkToFileList(
    imageLinkRecentlySelected!,
    fileName
  )
  changeFileImageCustomily(imageFileList, props.inputFileCustomily)
  exceedTheLimit.value = false

  imageUploadUrl.value = aiStyleTransferRecently.value[index]?.imageUploadUrl

  setTimeout(() => isSuccessUpload.value = true, 2000)
}

async function checkNumberOfRemainAiStyleTransfer() {
  const numberTransferred = getNumberStyleTransferToday()

  const productHandle = productStore.product?.handle
  const cacheKey = `${AI_STYLE_TRANSFER_LIMITED_CACHE_PREFIX}_${productHandle}`
  const cacheResponse = await CacheService.instance?.get(cacheKey)
  const aiTransferLimitedFromCache = getJSONfromCacheResponse(cacheResponse)
  if (numberTransferred >= aiTransferLimitedFromCache) {
    exceedTheLimit.value = true
  } else {
    exceedTheLimit.value = false
  }
}

function getNumberStyleTransferToday() {
  const today = new Date();

  const startOfDay = new Date(today.setHours(0, 0, 0, 0))

  const endOfDay = new Date(today.setHours(23, 59, 59, 999))

  const itemStyleTransferToday = aiStyleTransferRecently.value?.filter((item: any) => {
    const itemDate = new Date(Number(item.createdAt))
    return itemDate >= startOfDay && itemDate <= endOfDay
  });

  return itemStyleTransferToday?.length || 0
}

async function waitForImageReady(url: string, interval = 10000, maxAttempts = 60) {
  for (let i = 1; i <= maxAttempts; i++) {
    try {
      const res = await fetch(url, { method: "HEAD", cache: "no-cache" });

      if (res.status == 200) {
        return true;
      } else {
      }
    } catch (err) {
    }

    if (i < maxAttempts) {
      await new Promise(r => setTimeout(r, interval));
    }
  }
  return false;
}

function checkCustomYourName() {
  if (!props.customName) {
    return true
  }
  return customYourName.value?.trim() ? true : false
}

function changeImageNumberUploadOption(value: string) {
  imageNumberUpload.value = value
}

async function transferMultipleImage() {
  isEnoughCustomValueRequired.value = hasEnoughCustomValueRequired()
  if (!isEnoughCustomValueRequired.value) {
    return
  }

  hasCustomYourNameRequired.value = checkCustomYourName() 
  if (!hasCustomYourNameRequired.value) return

  isEnoughNumberImagesRequired.value = hasEnoughNumberImages()
  if (!isEnoughNumberImagesRequired.value) return

  await checkNumberOfRemainAiStyleTransfer()
  if (exceedTheLimit.value) {
    return
  }

  loading.value = true
  isSuccessUpload.value = false
  isShowMessage.value = false
  isRemoveImage.value = false
 
  const link = listImageUploadSelected.value.slice(0, Number(imageNumberUpload.value))?.map((item:any) => item.link)?.join(',')

  imageUploadUrl.value = link

  const promptCustom = customConfig.value.reduce((acc: any, item: any) => {
    acc[item.prompt] = item.customValue || '';
    return acc;
  }, {});

  const payload = {
    image: link!,
    templateId: templateIdSelected.value,
    id: props.customizationTemplate.id,
    custom_name: customYourName.value || '',
    prompt_custom: customConfig.value?.length ? JSON.stringify(promptCustom) : ''
  }

  transferImage(payload)
    .then(async (response) => {
      if (!response) {
        throw {
          message: errorTransferImageMessage,
        }
      }

      // vì link ảnh AI cần phải mất một thời gian mới có thể active
      // nên cần phải đợi một thời gian để link ảnh được active thì tiếp tục xử lí tiếp
      const isReady = await waitForImageReady(response)
      if (isReady) {
        imageAITransferedLink.value = response
      } else {
        throw {
          message: errorTransferImageMessage,
        }
      }

      const fileName = `${sibFileNameStyleTransferImagePrefix}-${templateIdSelected.value}-${props.customizationTemplate.id}`
      const imageFileList = await convertImageLinkToFileList(
        response!,
        fileName
      )
      changeFileImageCustomily(imageFileList, props.inputFileCustomily)
      isSuccessUpload.value = true
    })
    .catch((err) => {
      errorUploadMessage.value = err.message ?? errorUploadMessage.value
      isSuccessUpload.value = false
      Logger.log('Error when uploading image', err)
    })
    .finally(async () => {
      if (isSuccessUpload.value) {
        customilyUpLoadingHandler()
        await setAiStyleTransferRecently(imageAITransferedLink.value, imageUploadUrl.value)
        imageAITransferedLink.value = ''
        aiStyleTransferRecentlyIndexSelected.value = 0
      } else {
        loading.value = false
        isShowMessage.value = true
      }
    })
}

function hasEnoughNumberImages() {
  // kiểm tra đủ số image yêu cầu upload không
  const needed = Array.from({ length: Number(imageNumberUpload.value) }, (_, i) => i + 1);
  return needed.every(num => listImageUploadSelected.value.some((item: any) => item.photoNumber === num));
}

function hasEnoughCustomValueRequired() {
  if (!customConfig.value) return true
  const config = customConfig.value.find(
    (item:any) => !item.customValue || item.customValue.toString().trim() === ''
  );
  if (config) {
    titleCustomConfigHasNotValue.value = config?.title
    return false
  } else {
    titleCustomConfigHasNotValue.value = ''
    return true
  }
}
</script>

<style lang="scss">
.ai-style-transfer-recently {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  max-width: 320px;
  margin-bottom: 6px;

  &::-webkit-scrollbar-track {
    background-color: var(--background-color);
  }

  &::-webkit-scrollbar {
    height: 7px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #b4b4b4;
    border-radius: 1rem;
  }
  &__item {
    background-color: transparent;
    box-sizing: border-box;
    box-shadow: 0 0 2px 1px #0003;
    border: 3px solid #fff;
    width: 62px;
    height: 62px;
    transition: all 0.3s ease;
    cursor: pointer;
    margin: 5px 5px 3px 4px !important;

    &--active {
      border-color: #2f80ed;
    }

    &--image {
      width: 62px;
    }

    img {
      width: 56px;
      height: 56px;
      object-fit: contain;
    }

    &--active{
      position: relative;
      &::before {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        right: 2px;
        bottom: 0;
        background: url('@/assets/images/icons/check-mark.svg');
        background-repeat: no-repeat;
        background-size: cover;
        z-index: var(--base-z-index);
      }
    }
  }
}
.ai-style-transfer-history-title {
  font-weight: 600;
  margin: 14px 0 2px 0
}

.rate-limit-transfer-message {
  font-size: 12px;
  color: var(--color-secondary);
  margin-top: 8px;
  margin-bottom: 8px;
}

.customization-upload-image-wrapper + .cl_remove-image  {
  display: none;
}

.remove-custom-upload-image {
  padding: 14px;
}

.wrapper-style-transfer .cl_remove-image {
  display: none !important; 
}

.generate-loading {
  --loading-size: 16px;
  --loading-line-width: 2px;
  color: #ffffff;
  transform: translateX(-6px);
}

.generate-button {
  max-width: 170px;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.disable-button {
  pointer-events: none;
}
</style>